import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { Chart } from 'chart.js';

export interface JiraIssue {
  summary: string;
  issueKey: string;
  issueType: string;
  status: string;
  projectKey: string;
  projectName: string;
  priority: string;
  assignee: string;
  created: string;
  updated: string;
  resolved: string;
  sprint: string;
  storyPoints: number;
  originalEstimate: number;
  timeSpent: number;
  remainingEstimate: number;
  workRatio: number;
}

export interface SprintData {
  sprintName: string;
  totalIssues: number;
  completedIssues: number;
  totalStoryPoints: number;
  completedStoryPoints: number;
  commitment: number; // Planned story points at sprint start
  completed: number;  // Actual completed story points
  velocity: number;
  startDate: string;
  endDate: string;
  burndownData: BurndownPoint[];
}

export interface BurndownPoint {
  date: string;
  remainingStoryPoints: number;
  idealBurndown: number;
}

@Component({
  selector: 'app-jira-project-reports',
  templateUrl: './jira-project-reports.component.html',
  styleUrls: ['./jira-project-reports.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule
  ]
})
export class JiraProjectReportsComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [
    'issueKey', 'summary', 'issueType', 'status', 'sprint', 
    'storyPoints', 'originalEstimate', 'timeSpent', 'remainingEstimate', 'workRatio'
  ];
  dataSource: MatTableDataSource<JiraIssue>;
  
  jiraData: JiraIssue[] = [];
  sprintData: SprintData[] = [];
  selectedProject: string = '';
  selectedSprint: string = '';
  projects: string[] = [];
  sprints: string[] = [];
  
  // Charts
  velocityChart: any;
  burndownChart: any;
  storyPointsChart: any;
  timeTrackingChart: any;
  overallProgressChart: any;
  
  // Summary statistics
  totalIssues: number = 0;
  totalStoryPoints: number = 0;
  totalTimeSpent: number = 0;
  averageVelocity: number = 0;
  
  constructor(private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<JiraIssue>([]);
    
    // Check if Chart.js is available
    if (typeof Chart !== 'undefined') {
      console.log('Chart.js is loaded successfully');
    } else {
      console.error('Chart.js is not loaded');
    }
    
    // Test time formatting
    console.log('Time formatting test:');
    console.log('21600 seconds =', this.formatTime(21600)); // Should be 6h
    console.log('18000 seconds =', this.formatTime(18000)); // Should be 5h
    console.log('86400 seconds =', this.formatTime(86400)); // Should be 24h
    console.log('3600 seconds =', this.formatTime(3600));  // Should be 1h
    console.log('1800 seconds =', this.formatTime(1800));  // Should be 30m
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    
    // Set default sort for sprint data
    if (this.sprintData.length > 0) {
      // This will be handled by the matSort directive in the template
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      this.parseCSV(file);
    } else {
      this.snackBar.open('Please select a valid CSV file', 'Close', { duration: 3000 });
    }
  }

  parseCSV(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const csv = e.target.result;
        const lines = csv.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        
        this.jiraData = [];
        
        // Debug column mapping
        console.log('=== COLUMN MAPPING DEBUG ===');
        const storyPointsIndex = this.findColumnIndex(headers, 'Story point estimate');
        const originalEstimateIndex = this.findColumnIndex(headers, 'Original estimate');
        const timeSpentIndex = this.findColumnIndex(headers, 'Time Spent');
        const remainingEstimateIndex = this.findColumnIndex(headers, 'Remaining Estimate');
        const workRatioIndex = this.findColumnIndex(headers, 'Work Ratio');
        
        console.log('Column indices found:', {
          storyPointsIndex,
          originalEstimateIndex,
          timeSpentIndex,
          remainingEstimateIndex,
          workRatioIndex,
          headers: headers.filter(h => h.includes('Story') || h.includes('estimate') || h.includes('Time') || h.includes('Work'))
        });
        console.log('=== END COLUMN MAPPING DEBUG ===');
        
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim()) {
            const values = this.parseCSVLine(lines[i]);
            if (values.length >= headers.length) {
              
                              const issue: JiraIssue = {
                  summary: values[0] || '',
                  issueKey: values[1] || '',
                  issueType: values[3] || '',
                  status: values[4] || '',
                  projectKey: values[5] || '',
                  projectName: values[6] || '',
                  priority: values[11] || '',
                  assignee: values[13] || '',
                  created: values[19] || '',
                  updated: values[20] || '',
                  resolved: values[22] || '',
                  sprint: this.extractSprint(values),
                  storyPoints: storyPointsIndex >= 0 && storyPointsIndex < values.length ? this.parseNumber(values[storyPointsIndex]) : 0,
                  originalEstimate: originalEstimateIndex >= 0 && originalEstimateIndex < values.length ? this.parseNumber(values[originalEstimateIndex]) : 0,
                  timeSpent: timeSpentIndex >= 0 && timeSpentIndex < values.length ? this.parseNumber(values[timeSpentIndex]) : 0,
                  remainingEstimate: remainingEstimateIndex >= 0 && remainingEstimateIndex < values.length ? this.parseNumber(values[remainingEstimateIndex]) : 0,
                  workRatio: workRatioIndex >= 0 && workRatioIndex < values.length ? this.parseNumber(values[workRatioIndex]) : 0
                };
              this.jiraData.push(issue);
            }
          }
        }
        
        this.processData();
        this.snackBar.open(`Successfully loaded ${this.jiraData.length} issues`, 'Close', { duration: 3000 });
      } catch (error) {
        console.error('Error parsing CSV:', error);
        this.snackBar.open('Error parsing CSV file. Please check the file format.', 'Close', { duration: 5000 });
      }
    };
    reader.readAsText(file);
  }

  parseCSVLine(line: string): string[] {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  }

  findColumnIndex(headers: string[], columnName: string): number {
    // First try exact match
    let index = headers.findIndex(header => header === columnName);
    
    // If not found, try partial match
    if (index === -1) {
      index = headers.findIndex(header => header.includes(columnName));
    }
    
    // For custom fields, try different patterns
    if (index === -1) {
      if (columnName === 'Story point estimate') {
        // Try different variations of story points - prioritize Custom field (Story point estimate)
        index = headers.findIndex(header => 
          header === 'Custom field (Story point estimate)' ||
          header === 'Custom field (Story Points)' ||
          header.includes('Story point estimate') || 
          header.includes('Story Points') ||
          header.includes('Story point')
        );
      } else if (columnName === 'Original estimate') {
        index = headers.findIndex(header => 
          header.includes('Original estimate') || 
          header.includes('Original Estimate')
        );
      } else if (columnName === 'Time Spent') {
        index = headers.findIndex(header => 
          header.includes('Time Spent') || 
          header.includes('Time spent')
        );
      } else if (columnName === 'Remaining Estimate') {
        index = headers.findIndex(header => 
          header.includes('Remaining Estimate') || 
          header.includes('Remaining estimate')
        );
      } else if (columnName === 'Work Ratio') {
        index = headers.findIndex(header => 
          header.includes('Work Ratio') || 
          header.includes('Work ratio')
        );
      }
    }
    
    if (index === -1) {
      console.warn(`Column "${columnName}" not found in headers. Available headers:`, headers);
    } else {
      console.log(`Found column "${columnName}" at index ${index}: "${headers[index]}"`);
    }
    return index;
  }

  extractSprint(values: string[]): string {
    // Look for sprint information in the values
    for (let i = 0; i < values.length; i++) {
      if (values[i] && values[i].includes('Sprint')) {
        return values[i];
      }
    }
    
    // If not found in values, try to extract from specific columns
    // Sprint information might be in different columns depending on CSV export format
    const sprintColumns = [values[0], values[1], values[2], values[3], values[4], values[5]];
    for (const column of sprintColumns) {
      if (column && column.includes('Sprint')) {
        return column;
      }
    }
    
    // Try to find sprint in any field that contains "2025Q3" or similar patterns
    for (let i = 0; i < values.length; i++) {
      if (values[i] && (values[i].includes('2025Q3') || values[i].includes('2025Q2') || values[i].includes('2025Q4'))) {
        return values[i];
      }
    }
    
    return '';
  }

  parseNumber(value: string): number {
    if (!value) return 0;
    const num = parseFloat(value.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  }

  // Convert seconds to readable time format
  formatTime(seconds: number): string {
    if (seconds === 0) return '0h';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else {
      return `${seconds}s`;
    }
  }

  // Convert seconds to hours for calculations
  secondsToHours(seconds: number): number {
    return seconds / 3600;
  }

  processData() {
    this.dataSource.data = this.jiraData;
    
    // Extract unique projects and sprints
    this.projects = [...new Set(this.jiraData.map(issue => issue.projectName))];
    this.sprints = [...new Set(this.jiraData.map(issue => issue.sprint).filter(sprint => sprint))];
    
    // Calculate summary statistics
    this.totalIssues = this.jiraData.length;
    this.totalStoryPoints = this.jiraData.reduce((sum, issue) => sum + issue.storyPoints, 0);
    this.totalTimeSpent = this.secondsToHours(this.jiraData.reduce((sum, issue) => sum + issue.timeSpent, 0));
    
    console.log('Processed data:', {
      totalIssues: this.totalIssues,
      totalStoryPoints: this.totalStoryPoints,
      totalTimeSpent: this.totalTimeSpent,
      projects: this.projects,
      sprints: this.sprints
    });
    
    // Debug story points and status distribution
    this.debugStoryPointsData();
    
    // Process sprint data
    this.processSprintData();
    
    // Create charts only if we have data
    if (this.jiraData.length > 0) {
      this.createCharts();
    }
  }

  debugStoryPointsData() {
    console.log('=== STORY POINTS DEBUG ===');
    
    // Check story points distribution
    const issuesWithStoryPoints = this.jiraData.filter(issue => issue.storyPoints > 0);
    const issuesWithoutStoryPoints = this.jiraData.filter(issue => issue.storyPoints === 0);
    
    console.log('Story Points Summary:', {
      totalIssues: this.jiraData.length,
      issuesWithStoryPoints: issuesWithStoryPoints.length,
      issuesWithoutStoryPoints: issuesWithoutStoryPoints.length,
      totalStoryPoints: this.totalStoryPoints
    });
    
    // Check status distribution
    const statusCounts: { [key: string]: number } = {};
    const statusStoryPoints: { [key: string]: number } = {};
    
    this.jiraData.forEach(issue => {
      const status = issue.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
      statusStoryPoints[status] = (statusStoryPoints[status] || 0) + issue.storyPoints;
    });
    
    console.log('Status Distribution:', statusCounts);
    console.log('Story Points by Status:', statusStoryPoints);
    
    // Check sprint distribution
    const sprintCounts: { [key: string]: number } = {};
    const sprintStoryPoints: { [key: string]: number } = {};
    
    this.jiraData.forEach(issue => {
      const sprint = issue.sprint || 'No Sprint';
      sprintCounts[sprint] = (sprintCounts[sprint] || 0) + 1;
      sprintStoryPoints[sprint] = (sprintStoryPoints[sprint] || 0) + issue.storyPoints;
    });
    
    console.log('Sprint Distribution:', sprintCounts);
    console.log('Story Points by Sprint:', sprintStoryPoints);
    
    console.log('=== END DEBUG ===');
  }

  processSprintData() {
    const sprintMap = new Map<string, SprintData>();
    
    this.jiraData.forEach(issue => {
      if (issue.sprint) {
        if (!sprintMap.has(issue.sprint)) {
          sprintMap.set(issue.sprint, {
            sprintName: issue.sprint,
            totalIssues: 0,
            completedIssues: 0,
            totalStoryPoints: 0,
            completedStoryPoints: 0,
            commitment: 0,
            completed: 0,
            velocity: 0,
            startDate: this.extractSprintDate(issue.sprint),
            endDate: '',
            burndownData: []
          });
        }
        
        const sprint = sprintMap.get(issue.sprint)!;
        sprint.totalIssues++;
        sprint.totalStoryPoints += issue.storyPoints;
        
        // Commitment = total story points planned for the sprint
        sprint.commitment += issue.storyPoints;
        
        if (issue.status.toLowerCase().includes('done') || issue.status.toLowerCase().includes('closed')) {
          sprint.completedIssues++;
          sprint.completedStoryPoints += issue.storyPoints;
          sprint.completed += issue.storyPoints;
        }
      }
    });
    
    this.sprintData = Array.from(sprintMap.values());
    this.sprintData.forEach(sprint => {
      // Velocity = completed story points (this is the standard definition)
      sprint.velocity = sprint.completed;
      this.generateBurndownData(sprint);
    });
    
    // Sort by sprint start date (descending)
    this.sprintData.sort((a, b) => {
      const dateA = new Date(a.startDate || '1900-01-01');
      const dateB = new Date(b.startDate || '1900-01-01');
      return dateB.getTime() - dateA.getTime();
    });
    
    // Calculate average velocity (only from sprints with completed work)
    const sprintsWithVelocity = this.sprintData.filter(sprint => sprint.velocity > 0);
    this.averageVelocity = sprintsWithVelocity.length > 0 
      ? sprintsWithVelocity.reduce((sum, sprint) => sum + sprint.velocity, 0) / sprintsWithVelocity.length 
      : 0;
    
    console.log('Sprint data processed:', this.sprintData);
    console.log('Velocity calculation details:', {
      totalSprints: this.sprintData.length,
      sprintsWithVelocity: sprintsWithVelocity.length,
      averageVelocity: this.averageVelocity,
      velocityBreakdown: this.sprintData.map(s => ({
        sprint: s.sprintName,
        commitment: s.commitment,
        completed: s.completed,
        velocity: s.velocity
      }))
    });
  }

  extractSprintDate(sprintName: string): string {
    // Try to extract date from sprint name (e.g., "Pro-View 2025Q3 Sprint 10")
    const dateMatch = sprintName.match(/(\d{4})Q(\d)/);
    if (dateMatch) {
      const year = dateMatch[1];
      const quarter = dateMatch[2];
      const month = (parseInt(quarter) - 1) * 3 + 1;
      return `${year}-${month.toString().padStart(2, '0')}-01`;
    }
    return new Date().toISOString().split('T')[0];
  }

  generateBurndownData(sprint: SprintData) {
    // Generate sample burndown data (in a real scenario, this would come from actual sprint dates)
    const totalPoints = sprint.totalStoryPoints;
    const days = 14; // Assuming 2-week sprint
    
    for (let i = 0; i <= days; i++) {
      const date = new Date();
      date.setDate(date.getDate() - (days - i));
      
      const remainingPoints = Math.max(0, totalPoints - (sprint.velocity * i / days));
      const idealBurndown = totalPoints - (totalPoints * i / days);
      
      sprint.burndownData.push({
        date: date.toISOString().split('T')[0],
        remainingStoryPoints: remainingPoints,
        idealBurndown: idealBurndown
      });
    }
  }

  createCharts() {
    // Use setTimeout to ensure DOM elements are ready
    setTimeout(() => {
      this.createVelocityChart();
      this.createBurndownChart();
      this.createStoryPointsChart();
      this.createTimeTrackingChart();
      this.createOverallProgressChart();
    }, 100);
  }

  createVelocityChart() {
    if (typeof Chart === 'undefined') {
      console.error('Chart.js is not available');
      return;
    }
    
    const ctx = document.getElementById('velocityChart') as HTMLCanvasElement;
    if (ctx && this.sprintData.length > 0) {
      if (this.velocityChart) {
        this.velocityChart.destroy();
      }
      
      // Filter sprint data based on selected sprint
      let dataToShow = this.sprintData;
      let chartTitle = 'Sprint Velocity';
      
      if (this.selectedSprint) {
        // If a specific sprint is selected, show only that sprint
        const selectedSprintData = this.sprintData.find(sprint => sprint.sprintName === this.selectedSprint);
        if (selectedSprintData) {
          dataToShow = [selectedSprintData];
          chartTitle = `Sprint Velocity - ${this.selectedSprint}`;
        }
      }
      
      const velocities = dataToShow.map(sprint => sprint.velocity);
      const sprintsWithVelocity = velocities.filter(v => v > 0);
      const averageVelocity = sprintsWithVelocity.length > 0 
        ? sprintsWithVelocity.reduce((sum, vel) => sum + vel, 0) / sprintsWithVelocity.length 
        : 0;
      
      console.log('Creating velocity chart with data:', dataToShow.map(sprint => ({ 
        name: sprint.sprintName, 
        commitment: sprint.commitment,
        completed: sprint.completed,
        velocity: sprint.velocity
      })));
      console.log('Average velocity calculation:', {
        totalSprints: velocities.length,
        sprintsWithVelocity: sprintsWithVelocity.length,
        averageVelocity: averageVelocity,
        allVelocities: velocities,
        selectedSprint: this.selectedSprint
      });
      
      this.velocityChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: dataToShow.map(sprint => sprint.sprintName),
          datasets: [
            {
              label: 'Commitment',
              data: dataToShow.map(sprint => sprint.commitment),
              backgroundColor: 'rgba(128, 128, 128, 0.8)',
              borderColor: 'rgba(128, 128, 128, 1)',
              borderWidth: 1,
              type: 'bar'
            },
            {
              label: 'Completed',
              data: dataToShow.map(sprint => sprint.completed),
              backgroundColor: 'rgba(75, 192, 192, 0.8)',
              borderColor: 'rgba(75, 192, 192, 1)',
              borderWidth: 1,
              type: 'bar'
            },
            {
              label: 'Average Completed',
              data: dataToShow.map(() => averageVelocity),
              borderColor: 'rgba(0, 0, 0, 1)',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderWidth: 2,
              fill: false,
              type: 'line',
              pointRadius: 0
            }
          ]
        },
        options: {
          responsive: true,
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true
              },
              scaleLabel: {
                display: true,
                labelString: 'Story Points'
              }
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Sprint'
              }
            }]
          },
          title: {
            display: true,
            text: `${chartTitle} (Avg: ${averageVelocity.toFixed(1)} SP)`
          }
        }
      });
    }
  }

  createBurndownChart() {
    if (typeof Chart === 'undefined') {
      console.error('Chart.js is not available');
      return;
    }
    
    const ctx = document.getElementById('burndownChart') as HTMLCanvasElement;
    if (ctx && this.sprintData.length > 0) {
      if (this.burndownChart) {
        this.burndownChart.destroy();
      }
      
      // Use selected sprint or first sprint as default
      let sprint = this.sprintData[0];
      if (this.selectedSprint) {
        sprint = this.sprintData.find(s => s.sprintName === this.selectedSprint) || sprint;
      }
      
      const dates = sprint.burndownData.map(point => point.date);
      const actual = sprint.burndownData.map(point => point.remainingStoryPoints);
      const ideal = sprint.burndownData.map(point => point.idealBurndown);
      
      this.burndownChart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: dates,
          datasets: [
            {
              label: 'Actual Burndown',
              data: actual,
              borderColor: 'rgba(255, 99, 132, 1)',
              backgroundColor: 'rgba(255, 99, 132, 0.2)',
              fill: false
            },
            {
              label: 'Ideal Burndown',
              data: ideal,
              borderColor: 'rgba(75, 192, 192, 1)',
              backgroundColor: 'rgba(75, 192, 192, 0.2)',
              borderDash: [5, 5],
              fill: false
            }
          ]
        },
        options: {
          responsive: true,
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true
              },
              scaleLabel: {
                display: true,
                labelString: 'Remaining Story Points'
              }
            }],
            xAxes: [{
              scaleLabel: {
                display: true,
                labelString: 'Date'
              }
            }]
          },
          title: {
            display: true,
            text: `Burndown Chart - ${sprint.sprintName}`
          }
        }
      });
    }
  }

  createStoryPointsChart() {
    if (typeof Chart === 'undefined') {
      console.error('Chart.js is not available');
      return;
    }
    
    const ctx = document.getElementById('storyPointsChart') as HTMLCanvasElement;
    if (ctx) {
      if (this.storyPointsChart) {
        this.storyPointsChart.destroy();
      }
      
      const statusData = this.getStatusDistribution();
      console.log('Creating story points chart with data:', statusData);
      
      this.storyPointsChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: Object.keys(statusData),
          datasets: [{
            data: Object.values(statusData),
            backgroundColor: [
              'rgba(75, 192, 192, 0.8)',
              'rgba(255, 205, 86, 0.8)',
              'rgba(255, 99, 132, 0.8)',
              'rgba(54, 162, 235, 0.8)',
              'rgba(153, 102, 255, 0.8)'
            ]
          }]
        },
        options: {
          responsive: true,
          title: {
            display: true,
            text: 'Story Points by Status'
          },
          legend: {
            position: 'bottom'
          }
        }
      });
    }
  }

  getStatusDistribution(): { [key: string]: number } {
    const distribution: { [key: string]: number } = {};
    
    // Use filtered data if sprint is selected
    const dataToUse = this.selectedSprint ? 
      this.jiraData.filter(issue => issue.sprint === this.selectedSprint) : 
      this.jiraData;
    
    dataToUse.forEach(issue => {
      const status = issue.status || 'Unknown';
      distribution[status] = (distribution[status] || 0) + issue.storyPoints;
    });
    
    return distribution;
  }

  createTimeTrackingChart() {
    if (typeof Chart === 'undefined') {
      console.error('Chart.js is not available');
      return;
    }
    
    const ctx = document.getElementById('timeTrackingChart') as HTMLCanvasElement;
    if (ctx && this.jiraData.length > 0) {
      if (this.timeTrackingChart) {
        this.timeTrackingChart.destroy();
      }
      
      const dataToUse = this.selectedSprint ? 
        this.jiraData.filter(issue => issue.sprint === this.selectedSprint) : 
        this.jiraData;
      
      const totalOriginalEstimate = dataToUse.reduce((sum, issue) => sum + issue.originalEstimate, 0);
      const totalTimeSpent = dataToUse.reduce((sum, issue) => sum + issue.timeSpent, 0);
      const totalRemaining = dataToUse.reduce((sum, issue) => sum + issue.remainingEstimate, 0);
      
      this.timeTrackingChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: ['Original Estimate', 'Time Spent', 'Remaining'],
          datasets: [{
            label: 'Hours',
            data: [
              this.secondsToHours(totalOriginalEstimate),
              this.secondsToHours(totalTimeSpent),
              this.secondsToHours(totalRemaining)
            ],
            backgroundColor: [
              'rgba(54, 162, 235, 0.8)',
              'rgba(75, 192, 192, 0.8)',
              'rgba(255, 99, 132, 0.8)'
            ],
            borderColor: [
              'rgba(54, 162, 235, 1)',
              'rgba(75, 192, 192, 1)',
              'rgba(255, 99, 132, 1)'
            ],
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true
              },
              scaleLabel: {
                display: true,
                labelString: 'Hours'
              }
            }]
          },
          title: {
            display: true,
            text: 'Time Tracking Overview'
          }
        }
      });
    }
  }

  createOverallProgressChart() {
    if (typeof Chart === 'undefined') {
      console.error('Chart.js is not available');
      return;
    }
    
    const ctx = document.getElementById('overallProgressChart') as HTMLCanvasElement;
    if (ctx && this.jiraData.length > 0) {
      if (this.overallProgressChart) {
        this.overallProgressChart.destroy();
      }
      
      const dataToUse = this.selectedSprint ? 
        this.jiraData.filter(issue => issue.sprint === this.selectedSprint) : 
        this.jiraData;
      
      const totalPlanned = dataToUse.length;
      const completed = dataToUse.filter(issue => 
        issue.status.toLowerCase().includes('done') || 
        issue.status.toLowerCase().includes('closed')
      ).length;
      const inProgress = dataToUse.filter(issue => 
        issue.status.toLowerCase().includes('progress')
      ).length;
      const toDo = totalPlanned - completed - inProgress;
      
      this.overallProgressChart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Completed', 'In Progress', 'To Do'],
          datasets: [{
            data: [completed, inProgress, toDo],
            backgroundColor: [
              'rgba(75, 192, 192, 0.8)',
              'rgba(255, 205, 86, 0.8)',
              'rgba(255, 99, 132, 0.8)'
            ],
            borderColor: [
              'rgba(75, 192, 192, 1)',
              'rgba(255, 205, 86, 1)',
              'rgba(255, 99, 132, 1)'
            ],
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          title: {
            display: true,
            text: 'Overall Progress'
          },
          legend: {
            position: 'bottom'
          }
        }
      });
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  filterByProject(project: string) {
    this.selectedProject = project;
    this.applyFilters();
  }

  filterBySprint(sprint: string) {
    this.selectedSprint = sprint;
    this.applyFilters();
  }

  applyFilters() {
    let filteredData = this.jiraData;
    
    if (this.selectedProject) {
      filteredData = filteredData.filter(issue => issue.projectName === this.selectedProject);
    }
    
    if (this.selectedSprint) {
      filteredData = filteredData.filter(issue => issue.sprint === this.selectedSprint);
    }
    
    this.dataSource.data = filteredData;
    
    // Recreate charts with filtered data
    if (filteredData.length > 0) {
      this.createCharts();
    }
  }

  exportToCSV() {
    const headers = this.displayedColumns.join(',');
    const csvContent = [headers, ...this.dataSource.data.map(row => 
      this.displayedColumns.map(col => row[col as keyof JiraIssue]).join(',')
    )].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'jira-project-report.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
